import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './ui/command';
import { Calendar } from './ui/calendar';
import { BigCalendar } from './ui/big-calendar';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { CalendarIcon, MapPin, Users, Plane, Plus, Minus, X, SlidersHorizontal, ChevronDown, Sun, Sunset, Moon, Clock, Briefcase, Link } from 'lucide-react';
import { format, addDays } from 'date-fns';
import { airports, airportGroups, airlines } from '../mock';
import { cn } from '../lib/utils';

export const FlightSearch = ({ onSearch, initialData, isInline = false, globalCurrency = 'GBP', onDepartureChange }) => {
  // Default airports
  const defaultFromAirport = { code: 'LON', name: 'Any London Airport', city: 'London', country: 'United Kingdom', isGroup: true };
  const defaultToAirport = { code: 'DXB', name: 'Any Dubai Airport', city: 'Dubai', country: 'United Arab Emirates', isGroup: true };
  
  const [tripType, setTripType] = useState('round-trip');
  const [fromAirport, setFromAirport] = useState(defaultFromAirport);
  const [toAirport, setToAirport] = useState(defaultToAirport);
  const [departDate, setDepartDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [adults, setAdults] = useState(1);
  const [youth, setYouth] = useState(0);
  const [children, setChildren] = useState(0);
  const [infants, setInfants] = useState(0);
  const [travelClass, setTravelClass] = useState('economy');
  const [directFlights, setDirectFlights] = useState(false);
  const [flexiDates, setFlexiDates] = useState(false);
  const [selectedAirline, setSelectedAirline] = useState(null);
  const [openFrom, setOpenFrom] = useState(false);
  const [openTo, setOpenTo] = useState(false);
  const [openPassengers, setOpenPassengers] = useState(false);
  const [openDatePicker, setOpenDatePicker] = useState(false);
  const [openAirline, setOpenAirline] = useState(false);
  const [fromSearchTerm, setFromSearchTerm] = useState('');
  const [toSearchTerm, setToSearchTerm] = useState('');
  const [multiCitySearchTerms, setMultiCitySearchTerms] = useState({});
  const [multiCityPopovers, setMultiCityPopovers] = useState({});
  const [multiCityLegs, setMultiCityLegs] = useState([
    { from: null, to: null, date: null },
    { from: null, to: null, date: null }
  ]);
  
  // Advanced filter state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [maxStops, setMaxStops] = useState(null); // null = any, 0 = direct, 1 = 1 stop, 2 = 2+ stops
  const [maxPrice, setMaxPrice] = useState('');
  const [departureTimeRange, setDepartureTimeRange] = useState(null); // morning, afternoon, evening, night
  const [refundableOnly, setRefundableOnly] = useState(false);
  const [preferredAirlines, setPreferredAirlines] = useState([]);
  
  // New filters - Baggage and Connection Length
  const [baggageFilter, setBaggageFilter] = useState(null); // null, '2_pieces', 'hand_only'
  const [connectionOutbound, setConnectionOutbound] = useState(null); // none, short, relaxed, long, very_long
  const [connectionReturn, setConnectionReturn] = useState(null); // none, short, relaxed, long, very_long
  
  // Currency comes from global state (Header)
  const selectedCurrency = globalCurrency;
  
  // Fare calendar state
  const [fares, setFares] = useState({});
  const [faresLoading, setFaresLoading] = useState(false);

  // Load initial data when modifying search
  useEffect(() => {
    if (initialData) {
      // Restore trip type
      if (initialData.return_date) {
        setTripType('round-trip');
      } else {
        setTripType('one-way');
      }
      
      // Find and set airports
      const allOptions = [...airportGroups.map(g => ({ ...g, isGroup: true })), ...airports];
      const fromApt = allOptions.find(a => a.code === initialData.origin);
      const toApt = allOptions.find(a => a.code === initialData.destination);
      if (fromApt) setFromAirport(fromApt);
      if (toApt) setToAirport(toApt);
      
      // Set dates
      if (initialData.departure_date) {
        setDepartDate(new Date(initialData.departure_date));
      }
      if (initialData.return_date) {
        setReturnDate(new Date(initialData.return_date));
      }
      
      // Set passengers
      if (initialData.adults) setAdults(initialData.adults);
      if (initialData.youth) setYouth(initialData.youth);
      if (initialData.children) setChildren(initialData.children);
      if (initialData.infants) setInfants(initialData.infants);
      
      // Set travel class
      if (initialData.travel_class) setTravelClass(initialData.travel_class);
      
      // Set options
      if (initialData.direct_flights !== undefined) setDirectFlights(initialData.direct_flights);
      if (initialData.flexiDates !== undefined) setFlexiDates(initialData.flexiDates);
      
      // Set airline if any
      if (initialData.airline) setSelectedAirline(initialData.airline);
    }
  }, [initialData]);

  // Notify parent when departure airport changes (for header currency suggestion)
  useEffect(() => {
    if (onDepartureChange && fromAirport?.code) {
      onDepartureChange(fromAirport.code);
    }
  }, [fromAirport, onDepartureChange]);

  // Fetch fares from backend with caching
  const fetchFaresFromBackend = React.useCallback(async (origin, destination) => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const today = format(new Date(), 'yyyy-MM-dd');
      
      const response = await fetch(`${API_URL}/api/flights/fare-calendar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          origin: origin,
          destination: destination,
          departure_date: today,
          one_way: tripType === 'one-way',
          duration: 7
        })
      });
      
      const data = await response.json();
      
      if (data.success && data.data) {
        return data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching fare calendar:', error);
      return null;
    }
  }, [tripType]);

  // Load fares from backend when date picker opens
  useEffect(() => {
    if (!fromAirport || !toAirport || !openDatePicker) return;
    if (tripType === 'multi-city') return;
    
    const originCode = fromAirport.isGroup && fromAirport.airports?.length > 0 
      ? fromAirport.airports[0] 
      : fromAirport.code;
    const destCode = toAirport.isGroup && toAirport.airports?.length > 0 
      ? toAirport.airports[0] 
      : toAirport.code;
    
    if (!originCode || !destCode) return;
    
    setFaresLoading(true);
    
    fetchFaresFromBackend(originCode, destCode)
      .then((fareData) => {
        if (fareData) {
          setFares(fareData);
        } else {
          // Fallback: generate mock data if API fails
          const mockFares = {};
          const today = new Date();
          const baseFare = 150 + Math.random() * 100;
          
          for (let i = 0; i < 180; i++) {
            const date = addDays(today, i);
            const dateStr = format(date, 'yyyy-MM-dd');
            const dayOfWeek = date.getDay();
            let fare = baseFare;
            
            if (dayOfWeek === 5 || dayOfWeek === 6) fare += 30 + Math.random() * 40;
            fare += (Math.random() - 0.5) * 60;
            
            const month = date.getMonth();
            if (month === 11 || month === 6 || month === 7) fare += 50 + Math.random() * 50;
            if (Math.random() < 0.08) fare = 80 + Math.random() * 50;
            if (Math.random() < 0.05) fare = 350 + Math.random() * 100;
            
            mockFares[dateStr] = Math.round(fare);
          }
          setFares(mockFares);
        }
      })
      .finally(() => {
        setFaresLoading(false);
      });
  }, [fromAirport, toAirport, openDatePicker, tripType, fetchFaresFromBackend]);

  // Combine airport groups and individual airports for search
  // London airports should appear first by default
  const londonGroup = airportGroups.find(g => g.code === 'LON');
  const londonAirports = airports.filter(a => a.city === 'London');
  const otherGroups = airportGroups.filter(g => g.code !== 'LON').map(group => ({ ...group, isGroup: true }));
  const otherAirports = airports.filter(a => a.city !== 'London');
  
  // Swap origin and destination airports
  const swapAirports = () => {
    const temp = fromAirport;
    setFromAirport(toAirport);
    setToAirport(temp);
  };

  // Default order: London group -> London airports -> Other groups -> Other airports
  const allAirportOptions = [
    ...(londonGroup ? [{ ...londonGroup, isGroup: true }] : []),
    ...londonAirports,
    ...otherGroups,
    ...otherAirports
  ];

  // Popular destinations to show by default when no search term
  const popularDestinations = [
    // Major UAE/Middle East
    { code: 'DXB', name: 'Dubai Intl', city: 'Dubai', country: 'United Arab Emirates' },
    { code: 'AUH', name: 'Abu Dhabi Intl', city: 'Abu Dhabi', country: 'United Arab Emirates' },
    { code: 'DOH', name: 'Hamad Intl', city: 'Doha', country: 'Qatar' },
    // Saudi Arabia (Umrah destinations)
    { code: 'JED', name: 'King Abdulaziz Intl', city: 'Jeddah', country: 'Saudi Arabia' },
    { code: 'MED', name: 'Prince Mohammad Bin Abdulaziz Intl', city: 'Madinah', country: 'Saudi Arabia' },
    // Europe
    { code: 'CDG', name: 'Paris Charles de Gaulle', city: 'Paris', country: 'France' },
    { code: 'FCO', name: 'Leonardo da Vinci Fiumicino', city: 'Rome', country: 'Italy' },
    { code: 'BCN', name: 'Barcelona El Prat', city: 'Barcelona', country: 'Spain' },
    { code: 'AMS', name: 'Amsterdam Schiphol', city: 'Amsterdam', country: 'Netherlands' },
    { code: 'IST', name: 'Istanbul Airport', city: 'Istanbul', country: 'Turkey' },
    // North America
    { code: 'JFK', name: 'John F Kennedy Intl', city: 'New York', country: 'United States' },
    { code: 'LAX', name: 'Los Angeles Intl', city: 'Los Angeles', country: 'United States' },
    { code: 'YYZ', name: 'Toronto Pearson Intl', city: 'Toronto', country: 'Canada' },
    // Asia
    { code: 'BKK', name: 'Suvarnabhumi Airport', city: 'Bangkok', country: 'Thailand' },
    { code: 'SIN', name: 'Singapore Changi', city: 'Singapore', country: 'Singapore' },
  ];

  // Custom filter function for airport search - supports IATA code, city, country, and name
  const filterAirports = (options, searchTerm) => {
    // When no search term or too short, show popular destinations
    if (!searchTerm || searchTerm.trim() === '') {
      return popularDestinations;
    }
    
    const term = searchTerm.toLowerCase().trim();
    
    // For short terms (1 char), show filtered popular destinations
    if (term.length < 2) {
      return popularDestinations.filter(opt => 
        opt.code.toLowerCase().startsWith(term) ||
        opt.city.toLowerCase().startsWith(term) ||
        opt.country.toLowerCase().startsWith(term)
      );
    }
    
    // Special handling for city codes like LON, NYC, PAR - show group AND all individual airports
    const cityCodeGroups = {
      'lon': ['LON', 'LHR', 'LGW', 'LTN', 'STN', 'LCY', 'SEN'],
      'nyc': ['NYC', 'JFK', 'LGA', 'EWR', 'HPN', 'ISP'],
      'par': ['PAR', 'CDG', 'ORY', 'BVA'],
      'mil': ['MIL', 'MXP', 'LIN', 'BGY'],
      'rom': ['ROM', 'FCO', 'CIA'],
      'ber': ['BER'],
      'sto': ['STO', 'ARN', 'BMA', 'NYO'],
      'tyo': ['TYO', 'HND', 'NRT'],
      'osa': ['OSA', 'KIX', 'ITM'],
      'sha': ['SHA', 'PVG'],
      'bjs': ['BJS', 'PEK', 'PKX'],
      'bkk': ['BKK', 'DMK'],
      'kul': ['KUL', 'SZB'],
      'dxb': ['DXB', 'DWC', 'SHJ'],
      'ist': ['IST', 'SAW'],
      'lax': ['LAX', 'BUR', 'ONT', 'SNA', 'LGB'],
      'sfo': ['SFO', 'OAK', 'SJC'],
      'was': ['DCA', 'IAD', 'BWI'],
      'chi': ['ORD', 'MDW'],
      'mia': ['MIA', 'FLL', 'PBI']
    };
    
    // Check if search term matches a city code
    if (cityCodeGroups[term]) {
      const codes = cityCodeGroups[term];
      const matches = options.filter(opt => codes.includes(opt.code));
      // Sort: group first, then individual airports
      return matches.sort((a, b) => {
        if (a.isGroup && !b.isGroup) return -1;
        if (!a.isGroup && b.isGroup) return 1;
        return codes.indexOf(a.code) - codes.indexOf(b.code);
      });
    }
    
    // First check for exact IATA code match (3 letters)
    if (term.length === 3) {
      const exactMatch = options.filter(opt => 
        opt.code && opt.code.toLowerCase() === term
      );
      if (exactMatch.length > 0) {
        // If it's a group code, also show the individual airports
        const group = exactMatch.find(m => m.isGroup);
        if (group && group.airports) {
          const individualAirports = options.filter(opt => 
            !opt.isGroup && group.airports.includes(opt.code)
          );
          return [group, ...individualAirports];
        }
        return exactMatch;
      }
    }
    
    // Check if search term matches a country name (prioritize country search)
    const countryMatches = options.filter(opt => {
      const country = opt.country ? opt.country.toLowerCase() : '';
      // Match if country starts with term or contains the full term as a word
      return country.startsWith(term) || 
             country.split(' ').some(word => word.startsWith(term));
    });
    
    // If country matches found and term length > 2, return only country matches
    if (countryMatches.length > 0 && term.length > 2) {
      // Sort groups first, then individual airports by city name
      return countryMatches.sort((a, b) => {
        if (a.isGroup && !b.isGroup) return -1;
        if (!a.isGroup && b.isGroup) return 1;
        const cityA = a.city || '';
        const cityB = b.city || '';
        return cityA.localeCompare(cityB);
      });
    }
    
    // Filter by IATA code starting with term, city name starting with term, or name containing term
    const generalMatches = options.filter(opt => {
      const code = opt.code ? opt.code.toLowerCase() : '';
      const city = opt.city ? opt.city.toLowerCase() : '';
      const name = opt.name ? opt.name.toLowerCase() : '';
      
      // Match if code starts with term, city starts with term, or name contains term
      return code.startsWith(term) || 
             city.startsWith(term) || 
             name.toLowerCase().includes(term);
    });
    
    // Sort: groups first, then by city name
    return generalMatches.sort((a, b) => {
      if (a.isGroup && !b.isGroup) return -1;
      if (!a.isGroup && b.isGroup) return 1;
      const cityA = a.city || '';
      const cityB = b.city || '';
      return cityA.localeCompare(cityB);
    });
  };

  // Default UK departure airports
  const ukDepartureAirports = [
    { code: 'LON', name: 'All London Airports', city: 'London', country: 'United Kingdom', isGroup: true, airports: ['LHR', 'LGW', 'LTN', 'STN', 'LCY', 'SEN'] },
    { code: 'LHR', name: 'London Heathrow', city: 'London', country: 'United Kingdom' },
    { code: 'LGW', name: 'London Gatwick', city: 'London', country: 'United Kingdom' },
    { code: 'LTN', name: 'London Luton', city: 'London', country: 'United Kingdom' },
    { code: 'STN', name: 'London Stansted', city: 'London', country: 'United Kingdom' },
    { code: 'LCY', name: 'London City Airport', city: 'London', country: 'United Kingdom' },
    { code: 'MAN', name: 'Manchester Airport', city: 'Manchester', country: 'United Kingdom' },
    { code: 'BHX', name: 'Birmingham Airport', city: 'Birmingham', country: 'United Kingdom' },
    { code: 'EDI', name: 'Edinburgh Airport', city: 'Edinburgh', country: 'United Kingdom' },
    { code: 'GLA', name: 'Glasgow Airport', city: 'Glasgow', country: 'United Kingdom' },
    { code: 'BRS', name: 'Bristol Airport', city: 'Bristol', country: 'United Kingdom' },
    { code: 'NCL', name: 'Newcastle Airport', city: 'Newcastle', country: 'United Kingdom' },
  ];

  // Filter function for FROM airport with UK airports as default
  const filterFromAirports = (options, searchTerm) => {
    if (!searchTerm || searchTerm.trim() === '') {
      return ukDepartureAirports;
    }
    return filterAirports(options, searchTerm);
  };

  // Filtered airport lists - FROM shows UK airports, TO shows popular destinations
  const filteredFromAirports = filterFromAirports(allAirportOptions, fromSearchTerm);
  const filteredToAirports = filterAirports(allAirportOptions, toSearchTerm);
  
  // Function to get filtered airports for multi-city legs
  const getFilteredMultiCityAirports = (legIndex, field) => {
    const searchKey = `${legIndex}-${field}`;
    const searchTerm = multiCitySearchTerms[searchKey] || '';
    // For 'from' field show UK airports by default, for 'to' field show popular destinations
    if (field === 'from') {
      return filterFromAirports(allAirportOptions, searchTerm);
    }
    return filterAirports(allAirportOptions, searchTerm);
  };
  
  const updateMultiCitySearchTerm = (legIndex, field, value) => {
    const searchKey = `${legIndex}-${field}`;
    setMultiCitySearchTerms(prev => ({
      ...prev,
      [searchKey]: value
    }));
  };

  // Helper functions for multi-city popover state
  const isMultiCityPopoverOpen = (legIndex, field) => {
    return multiCityPopovers[`${legIndex}-${field}`] || false;
  };

  const setMultiCityPopoverOpen = (legIndex, field, isOpen) => {
    setMultiCityPopovers(prev => ({
      ...prev,
      [`${legIndex}-${field}`]: isOpen
    }));
  };

  const removeMultiCityLeg = (index) => {
    if (multiCityLegs.length > 2) {
      const newLegs = multiCityLegs.filter((_, i) => i !== index);
      // Re-chain: update the "from" of the leg after the removed one
      if (index > 0 && index < newLegs.length) {
        newLegs[index] = { ...newLegs[index], from: newLegs[index - 1]?.to || null };
      }
      setMultiCityLegs(newLegs);
    }
  };

  const updateMultiCityLeg = (index, field, value) => {
    setMultiCityLegs(prevLegs => {
      const newLegs = [...prevLegs];
      
      // Update the current field
      newLegs[index] = { ...newLegs[index], [field]: value };
      
      // Auto-chain: When "to" is set, auto-fill next leg's "from"
      if (field === 'to' && value && index < newLegs.length - 1) {
        newLegs[index + 1] = { ...newLegs[index + 1], from: value };
      }
      
      // Auto-chain: When "date" is set, ensure next leg's date is cleared if it's before this date
      if (field === 'date' && value && index < newLegs.length - 1) {
        const nextLegDate = newLegs[index + 1].date;
        if (nextLegDate && nextLegDate < value) {
          newLegs[index + 1] = { ...newLegs[index + 1], date: null };
        }
      }
      
      return newLegs;
    });
  };

  // Add a new leg with auto-chaining
  const addMultiCityLeg = () => {
    if (multiCityLegs.length < 5) {
      const lastLeg = multiCityLegs[multiCityLegs.length - 1];
      // Auto-fill the new leg's "from" with the previous leg's "to"
      setMultiCityLegs([
        ...multiCityLegs, 
        { 
          from: lastLeg?.to || null, 
          to: null, 
          date: null 
        }
      ]);
    }
  };

  const totalPassengers = adults + youth + children + infants;

  const handleDepartDateSelect = (date) => {
    setDepartDate(date);
  };

  const handleReturnDateSelect = (date) => {
    setReturnDate(date);
  };
  
  const handleDateSelectionComplete = () => {
    setOpenDatePicker(false);
  };

  const handleSearch = () => {
    if (tripType === 'multi-city') {
      const validLegs = multiCityLegs.filter(leg => leg.from && leg.to && leg.date);
      if (validLegs.length >= 2) {
        // Format legs for API
        const formattedLegs = validLegs.map(leg => ({
          origin: leg.from.isGroup ? leg.from.code : leg.from.code,
          origin_airports: leg.from.isGroup ? leg.from.airports : [leg.from.code],
          destination: leg.to.isGroup ? leg.to.code : leg.to.code,
          destination_airports: leg.to.isGroup ? leg.to.airports : [leg.to.code],
          departure_date: format(leg.date, 'yyyy-MM-dd'),
          from: leg.from,
          to: leg.to
        }));
        
        onSearch({
          tripType,
          legs: formattedLegs,
          adults,
          youth,
          children,
          infants,
          travelClass,
          travel_class: travelClass,
          directFlights,
          direct_flights: directFlights,
          flexiDates: false, // Multi-city doesn't support flexi dates
          airline: selectedAirline,
          currency: selectedCurrency
        });
      }
    } else if (fromAirport && toAirport && departDate) {
      // Extract airport code(s) - handle both individual airports and groups
      // For groups, send all airports in the group
      const originCode = fromAirport.isGroup ? fromAirport.code : fromAirport.code;
      const originAirports = fromAirport.isGroup ? fromAirport.airports : [fromAirport.code];
      const destinationCode = toAirport.isGroup ? toAirport.code : toAirport.code;
      const destinationAirports = toAirport.isGroup ? toAirport.airports : [toAirport.code];
      
      onSearch({
        tripType,
        origin: originCode,
        destination: destinationCode,
        origin_airports: originAirports,
        destination_airports: destinationAirports,
        from: fromAirport,
        to: toAirport,
        departDate,
        departure_date: departDate ? format(departDate, 'yyyy-MM-dd') : null,
        returnDate: tripType === 'round-trip' ? returnDate : null,
        return_date: tripType === 'round-trip' && returnDate ? format(returnDate, 'yyyy-MM-dd') : null,
        adults,
        youth,
        children,
        infants,
        travelClass,
        travel_class: travelClass,
        directFlights,
        direct_flights: directFlights,
        flexiDates,
        airline: selectedAirline,
        currency: selectedCurrency,
        // Advanced filters
        max_stops: maxStops,
        max_price: maxPrice ? parseFloat(maxPrice) : null,
        departure_time_range: departureTimeRange,
        refundable_only: refundableOnly || null,
        // Combine selectedAirline and preferredAirlines for the filter
        preferred_airlines: selectedAirline 
          ? [selectedAirline.code, ...preferredAirlines.filter(a => a !== selectedAirline.code)]
          : (preferredAirlines.length > 0 ? preferredAirlines : null),
        // New filters - Baggage and Connection Length
        baggage_filter: baggageFilter,
        connection_outbound: connectionOutbound,
        connection_return: connectionReturn
      });
    }
  };

  // Inline mode - more compact design for results page
  if (isInline) {
    return (
      <div className="w-full">
        <Tabs value={tripType} onValueChange={setTripType} className="w-full">
          {/* Compact Trip Type Tabs for inline mode */}
          <div className="flex items-center gap-4 mb-4">
            <TabsList className="flex gap-1 h-auto p-1 bg-[#F5F5F5] rounded-lg">
              <TabsTrigger value="one-way" data-testid="inline-tab-one-way" className="text-xs py-2 px-4 text-[#222222] data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow-sm rounded-md font-medium transition-colors">
                One Way
              </TabsTrigger>
              <TabsTrigger value="round-trip" data-testid="inline-tab-return" className="text-xs py-2 px-4 text-[#222222] data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow-sm rounded-md font-medium transition-colors">
                Return
              </TabsTrigger>
              <TabsTrigger value="multi-city" data-testid="inline-tab-multi-city" className="text-xs py-2 px-4 text-[#222222] data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow-sm rounded-md font-medium transition-colors">
                Multi-City
              </TabsTrigger>
              <TabsTrigger value="umrah" data-testid="inline-tab-umrah-builder" className="text-xs py-2 px-4 text-white bg-[#FF6600] data-[state=active]:bg-[#E65C00] data-[state=active]:text-white rounded-md font-semibold transition-colors">
                Umrah Builder
              </TabsTrigger>
            </TabsList>
          </div>
          
          {/* Inline Search Fields - Single Row */}
          {(tripType === 'round-trip' || tripType === 'one-way') && (
            <TabsContent value={tripType} className="mt-0 space-y-3">
              <div className="flex flex-col lg:flex-row gap-2">
                {/* Origin & Destination */}
                <div className="flex-1 flex gap-0 relative">
                  {/* From */}
                  <Popover open={openFrom} onOpenChange={setOpenFrom}>
                    <PopoverTrigger asChild>
                      <button className="flex-1 h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-l-lg transition-all">
                        <div className="flex items-center gap-2">
                          <Plane className="h-4 w-4 text-brand-500 rotate-[-45deg]" />
                          <span className="font-semibold text-slate-800 text-sm truncate">
                            {fromAirport ? fromAirport.code : 'From'}
                          </span>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="City, airport or country" value={fromSearchTerm} onValueChange={setFromSearchTerm} />
                        <CommandEmpty>No airport found.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {filteredFromAirports.map((option) => (
                            <CommandItem key={option.code} value={option.code} onSelect={() => { setFromAirport(option); setFromSearchTerm(''); setOpenFrom(false); }}>
                              <span className="font-medium">{option.code}</span> - {option.city}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  
                  {/* Swap */}
                  <button onClick={swapAirports} className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10 w-8 h-8 rounded-full bg-white border border-slate-200 hover:border-brand-500 shadow-sm flex items-center justify-center">
                    <svg className="w-4 h-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  </button>

                  {/* To */}
                  <Popover open={openTo} onOpenChange={setOpenTo}>
                    <PopoverTrigger asChild>
                      <button className="flex-1 h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-r-lg border-l-0 transition-all">
                        <div className="flex items-center gap-2">
                          <MapPin className="h-4 w-4 text-brand-500" />
                          <span className="font-semibold text-slate-800 text-sm truncate">
                            {toAirport ? toAirport.code : 'To'}
                          </span>
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput placeholder="City, airport or country" value={toSearchTerm} onValueChange={setToSearchTerm} />
                        <CommandEmpty>No airport found.</CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-auto">
                          {filteredToAirports.map((option) => (
                            <CommandItem key={option.code} value={option.code} onSelect={() => { setToAirport(option); setToSearchTerm(''); setOpenTo(false); }}>
                              <span className="font-medium">{option.code}</span> - {option.city}
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Dates */}
                <Popover open={openDatePicker} onOpenChange={setOpenDatePicker}>
                  <PopoverTrigger asChild>
                    <button className="lg:w-[200px] h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-lg transition-all">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4 text-brand-500" />
                        <span className="font-semibold text-slate-800 text-sm">
                          {departDate ? format(departDate, 'd MMM') : 'Depart'}
                          {tripType === 'round-trip' && (returnDate ? ` - ${format(returnDate, 'd MMM')}` : ' - Return')}
                        </span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <BigCalendar
                      tripType={tripType === 'round-trip' ? 'round-trip' : 'one-way'}
                      departDate={departDate}
                      returnDate={returnDate}
                      onDepartSelect={(date) => setDepartDate(date)}
                      onReturnSelect={(date) => setReturnDate(date)}
                      onSelectionComplete={() => setOpenDatePicker(false)}
                    />
                  </PopoverContent>
                </Popover>

                {/* Passengers */}
                <Popover open={openPassengers} onOpenChange={setOpenPassengers}>
                  <PopoverTrigger asChild>
                    <button className="lg:w-[120px] h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-lg transition-all">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-brand-500" />
                        <span className="font-semibold text-slate-800 text-sm">{adults + youth + children + infants}</span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-72 p-3" align="end">
                    <div className="space-y-3">
                      {[
                        { label: 'Adults', sub: '16+', value: adults, setter: setAdults, min: 1 },
                        { label: 'Youth', sub: '12 - <16', value: youth, setter: setYouth, min: 0 },
                        { label: 'Children', sub: '02 - <12', value: children, setter: setChildren, min: 0 },
                        { label: 'Infants', sub: '0 - <02', value: infants, setter: setInfants, min: 0 }
                      ].map(({ label, sub, value, setter, min }) => (
                        <div key={label} className="flex items-center justify-between">
                          <div><div className="font-medium text-sm">{label}</div><div className="text-xs text-slate-500">{sub}</div></div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setter(Math.max(min, value - 1))} className="w-7 h-7 rounded-full border flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                            <span className="w-5 text-center text-sm font-medium">{value}</span>
                            <button onClick={() => setter(label === 'Infants' ? Math.min(adults, value + 1) : Math.min(9, value + 1))} className="w-7 h-7 rounded-full border flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                          </div>
                        </div>
                      ))}
                      <Button onClick={() => setOpenPassengers(false)} className="w-full bg-brand-600 hover:bg-brand-700 text-white text-sm h-9">Done</Button>
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Search Button */}
                <Button 
                  onClick={handleSearch} data-testid="search-flights-btn"
                  disabled={!fromAirport || !toAirport || !departDate || (tripType === 'round-trip' && !returnDate)}
                  className="lg:w-[120px] h-12 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg shadow-md"
                >
                  <svg className="w-5 h-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  Search
                </Button>
              </div>
              
              {/* Options Row - Bag only, Direct flights, Class of Service */}
              <div className="flex flex-wrap items-center gap-4 pt-2 border-t border-slate-100">
                {/* Bag only fares checkbox */}
                <label className="flex items-center gap-2 cursor-pointer group">
                  <Checkbox 
                    checked={baggageFilter === '2_pieces'} 
                    onCheckedChange={(checked) => setBaggageFilter(checked ? '2_pieces' : null)}
                    className="border-slate-300 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800">Bag only fares</span>
                </label>
                
                {/* Direct flights only checkbox */}
                <label htmlFor="direct-flights-inline" className="flex items-center gap-2 cursor-pointer group">
                  <Checkbox 
                    id="direct-flights-inline"
                    data-testid="direct-flights-checkbox"
                    checked={directFlights} 
                    onCheckedChange={setDirectFlights}
                    className="border-slate-300 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600"
                  />
                  <span className="text-sm text-slate-600 group-hover:text-slate-800">Direct flights only</span>
                </label>
                
                {/* Class of Service dropdown */}
                <div className="flex items-center gap-2">
                  <span className="text-sm text-slate-500">CLASS OF SERVICE</span>
                  <Select value={travelClass} onValueChange={setTravelClass}>
                    <SelectTrigger className="w-[160px] h-9 text-sm bg-slate-50 border-slate-200">
                      <SelectValue placeholder="Select class" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="economy">Economy</SelectItem>
                      <SelectItem value="premium_economy">Premium Economy</SelectItem>
                      <SelectItem value="business">Business Class</SelectItem>
                      <SelectItem value="first">First Class</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </TabsContent>
          )}
          
          {/* Multi-City Tab Content for Inline Mode */}
          <TabsContent value="multi-city" className="mt-0 space-y-3">
            <div className="space-y-3">
              {multiCityLegs.map((leg, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                  <div className="flex-1 flex gap-2">
                    {/* From */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-lg transition-all">
                          <div className="flex items-center gap-2">
                            <Plane className="h-4 w-4 text-brand-500 rotate-[-45deg]" />
                            <span className="font-semibold text-slate-800 text-sm truncate">
                              {leg.from?.code || 'From'}
                            </span>
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput placeholder="City or airport" />
                          <CommandEmpty>No airport found.</CommandEmpty>
                          <CommandGroup className="max-h-48 overflow-auto">
                            {allAirportOptions.map((option) => (
                              <CommandItem key={option.code} value={option.code} onSelect={() => updateMultiCityLeg(index, 'from', option)}>
                                <span className="font-medium">{option.code}</span> - {option.city}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    
                    {/* To */}
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="flex-1 h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-lg transition-all">
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-brand-500" />
                            <span className="font-semibold text-slate-800 text-sm truncate">
                              {leg.to?.code || 'To'}
                            </span>
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-[280px] p-0" align="start">
                        <Command shouldFilter={false}>
                          <CommandInput placeholder="City or airport" />
                          <CommandEmpty>No airport found.</CommandEmpty>
                          <CommandGroup className="max-h-48 overflow-auto">
                            {allAirportOptions.map((option) => (
                              <CommandItem key={option.code} value={option.code} onSelect={() => updateMultiCityLeg(index, 'to', option)}>
                                <span className="font-medium">{option.code}</span> - {option.city}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </Command>
                      </PopoverContent>
                    </Popover>
                    
                    {/* Date */}
                    <Popover 
                      open={multiCityPopovers[`date_${index}`]} 
                      onOpenChange={(open) => setMultiCityPopovers(prev => ({...prev, [`date_${index}`]: open}))}
                    >
                      <PopoverTrigger asChild>
                        <button className="w-[140px] h-12 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-brand-400 rounded-lg transition-all">
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-4 w-4 text-brand-500" />
                            <span className="font-semibold text-slate-800 text-sm">
                              {leg.date ? format(leg.date, 'd MMM') : 'Date'}
                            </span>
                          </div>
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <BigCalendar
                          tripType="one-way"
                          departDate={leg.date}
                          onDepartSelect={(date) => updateMultiCityLeg(index, 'date', date)}
                          onSelectionComplete={() => setMultiCityPopovers(prev => ({...prev, [`date_${index}`]: false}))}
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                  
                  {/* Remove leg button */}
                  {multiCityLegs.length > 2 && (
                    <button
                      onClick={() => removeMultiCityLeg(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-full transition-colors"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
              
              {/* Add leg and Search buttons */}
              <div className="flex items-center gap-3 flex-wrap">
                {multiCityLegs.length < 6 && (
                  <button
                    onClick={addMultiCityLeg}
                    className="flex items-center gap-1 text-sm text-brand-600 hover:text-brand-700 font-medium"
                  >
                    <Plus className="h-4 w-4" />
                    Add flight
                  </button>
                )}
                
                <div className="flex-1" />
                
                {/* Passengers */}
                <Popover open={openPassengers} onOpenChange={setOpenPassengers}>
                  <PopoverTrigger asChild>
                    <button className="w-[100px] h-10 px-3 text-left bg-slate-50 hover:bg-slate-100 border border-slate-200 rounded-lg transition-all">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-brand-500" />
                        <span className="font-semibold text-slate-800 text-sm">{adults + youth + children + infants}</span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-3" align="end">
                    <div className="space-y-2">
                      {[
                        { label: 'Adults', sub: '16+', value: adults, setter: setAdults, min: 1 },
                        { label: 'Youth', sub: '12 - <16', value: youth, setter: setYouth, min: 0 },
                        { label: 'Children', sub: '02 - <12', value: children, setter: setChildren, min: 0 },
                        { label: 'Infants', sub: '0 - <02', value: infants, setter: setInfants, min: 0 }
                      ].map(({ label, sub, value, setter, min }) => (
                        <div key={label} className="flex items-center justify-between">
                          <div><span className="text-sm font-medium">{label}</span><span className="text-xs text-slate-500 ml-1">({sub})</span></div>
                          <div className="flex items-center gap-2">
                            <button onClick={() => setter(Math.max(min, value - 1))} className="w-6 h-6 rounded-full border flex items-center justify-center"><Minus className="h-3 w-3" /></button>
                            <span className="w-4 text-center text-sm">{value}</span>
                            <button onClick={() => setter(Math.min(9, value + 1))} className="w-6 h-6 rounded-full border flex items-center justify-center"><Plus className="h-3 w-3" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                
                {/* Search Button */}
                <Button 
                  onClick={handleSearch} data-testid="search-flights-btn"
                  disabled={!multiCityLegs.every(leg => leg.from && leg.to && leg.date)}
                  className="h-10 px-6 bg-brand-600 hover:bg-brand-700 text-white font-semibold rounded-lg"
                >
                  Search
                </Button>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    );
  }

  return (
    <Card className="w-full max-w-5xl mx-auto p-0 bg-white shadow-2xl border-0 rounded-2xl overflow-hidden">
      {/* Trip Type Tabs - Skyscanner Style */}
      <Tabs value={tripType} onValueChange={setTripType} className="w-full">
        <div className="bg-[#FF6600] px-4 py-3">
          <TabsList className="flex w-full gap-1 h-auto p-1 bg-white/10 rounded-xl">
            <TabsTrigger value="one-way" data-testid="tab-one-way" className="flex-1 text-xs sm:text-sm py-2.5 px-2 sm:px-4 text-white data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow rounded-lg font-medium transition-colors">
              One Way
            </TabsTrigger>
            <TabsTrigger value="round-trip" data-testid="tab-return" className="flex-1 text-xs sm:text-sm py-2.5 px-2 sm:px-4 text-white data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow rounded-lg font-medium transition-colors">
              Return
            </TabsTrigger>
            <TabsTrigger value="multi-city" data-testid="tab-multi-city" className="flex-1 text-xs sm:text-sm py-2.5 px-2 sm:px-4 text-white data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow rounded-lg font-medium transition-colors">
              Multi-City
            </TabsTrigger>
            <TabsTrigger value="umrah" data-testid="tab-umrah-builder" className="flex-1 text-xs sm:text-sm py-2.5 px-2 sm:px-4 text-white data-[state=active]:bg-white data-[state=active]:text-[#FF6600] data-[state=active]:shadow rounded-lg font-semibold transition-colors">
              Umrah Builder
            </TabsTrigger>
          </TabsList>
        </div>
        
        {/* Round Trip and One Way - Skyscanner Layout */}
        {(tripType === 'round-trip' || tripType === 'one-way') && (
          <TabsContent value={tripType} className="p-4 sm:p-6 space-y-4 mt-0">
            {/* Main Search Row - Horizontal on Desktop */}
            <div className="flex flex-col lg:flex-row gap-3">
              {/* Origin & Destination Group */}
              <div className="flex-1 flex flex-col sm:flex-row gap-0 relative">
                {/* From Airport */}
                <div className="flex-1">
                  <Popover open={openFrom} onOpenChange={setOpenFrom}>
                    <PopoverTrigger asChild>
                      <button
                        className="w-full h-16 sm:h-[72px] px-4 text-left bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 hover:border-brand-400 rounded-t-xl sm:rounded-l-xl sm:rounded-tr-none transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <div className="text-xs text-slate-500 font-medium mb-1">From</div>
                        <div className="flex items-center gap-2">
                          <Plane className="h-5 w-5 text-brand-500 rotate-[-45deg]" />
                          {fromAirport ? (
                            <div className="truncate">
                              <span className="font-bold text-slate-800 text-lg">{fromAirport.code}</span>
                              <span className="text-slate-500 text-sm ml-2 hidden sm:inline">{fromAirport.isGroup ? fromAirport.name : fromAirport.city}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">Select airport</span>
                          )}
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] sm:w-[400px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput 
                          placeholder="City, airport or country" 
                          value={fromSearchTerm}
                          onValueChange={setFromSearchTerm}
                          className="h-12"
                        />
                        <CommandEmpty>No airport found.</CommandEmpty>
                        <CommandGroup className="max-h-72 overflow-auto">
                          {filteredFromAirports.map((option) => (
                            <CommandItem
                              key={option.isGroup ? `group-${option.code}` : `airport-${option.code}`}
                              value={option.code}
                              onSelect={() => {
                                setFromAirport(option);
                                setFromSearchTerm('');
                                setOpenFrom(false);
                              }}
                              className="py-3 cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                                  <Plane className="h-5 w-5 text-brand-600" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-800">
                                    {option.isGroup ? option.name : option.city}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {option.code} · {option.isGroup ? `All airports` : option.country}
                                  </span>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
                
                {/* Swap Button */}
                <button 
                  onClick={swapAirports}
                  className="absolute left-1/2 top-[56px] sm:top-1/2 -translate-x-1/2 sm:-translate-y-1/2 z-10 w-10 h-10 rounded-full bg-white border-2 border-slate-200 hover:border-brand-500 hover:bg-brand-50 shadow-md flex items-center justify-center transition-all group"
                >
                  <svg className="w-5 h-5 text-slate-400 group-hover:text-brand-600 transition-colors rotate-90 sm:rotate-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                  </svg>
                </button>

                {/* To Airport */}
                <div className="flex-1">
                  <Popover open={openTo} onOpenChange={setOpenTo}>
                    <PopoverTrigger asChild>
                      <button
                        className="w-full h-16 sm:h-[72px] px-4 text-left bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 hover:border-brand-400 rounded-b-xl sm:rounded-r-xl sm:rounded-bl-none border-t-0 sm:border-t-2 sm:border-l-0 transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
                      >
                        <div className="text-xs text-slate-500 font-medium mb-1">To</div>
                        <div className="flex items-center gap-2">
                          <MapPin className="h-5 w-5 text-brand-500" />
                          {toAirport ? (
                            <div className="truncate">
                              <span className="font-bold text-slate-800 text-lg">{toAirport.code}</span>
                              <span className="text-slate-500 text-sm ml-2 hidden sm:inline">{toAirport.isGroup ? toAirport.name : toAirport.city}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400">Select airport</span>
                          )}
                        </div>
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[320px] sm:w-[400px] p-0" align="start">
                      <Command shouldFilter={false}>
                        <CommandInput 
                          placeholder="City, airport or country" 
                          value={toSearchTerm}
                          onValueChange={setToSearchTerm}
                          className="h-12"
                        />
                        <CommandEmpty>No airport found.</CommandEmpty>
                        <CommandGroup className="max-h-72 overflow-auto">
                          {filteredToAirports.map((option) => (
                            <CommandItem
                              key={option.isGroup ? `group-${option.code}` : `airport-${option.code}`}
                              value={option.code}
                              onSelect={() => {
                                setToAirport(option);
                                setToSearchTerm('');
                                setOpenTo(false);
                              }}
                              className="py-3 cursor-pointer"
                            >
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-full bg-brand-50 flex items-center justify-center">
                                  <MapPin className="h-5 w-5 text-brand-600" />
                                </div>
                                <div className="flex flex-col">
                                  <span className="font-semibold text-slate-800">
                                    {option.isGroup ? option.name : option.city}
                                  </span>
                                  <span className="text-xs text-slate-500">
                                    {option.code} · {option.isGroup ? `All airports` : option.country}
                                  </span>
                                </div>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </Command>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>

              {/* Dates Group */}
              <div className="lg:w-[280px]">
                <Popover open={openDatePicker} onOpenChange={setOpenDatePicker}>
                  <PopoverTrigger asChild>
                    <button className="w-full h-16 sm:h-[72px] px-4 text-left bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 hover:border-brand-400 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                      <div className="flex items-center justify-between h-full">
                        <div className="flex-1">
                          <div className="text-xs text-slate-500 font-medium mb-1">Depart</div>
                          <div className="flex items-center gap-2">
                            <CalendarIcon className="h-5 w-5 text-brand-500" />
                            <span className={cn("font-bold text-lg", departDate ? "text-slate-800" : "text-slate-400")}>
                              {departDate ? format(departDate, 'd MMM') : 'Add date'}
                            </span>
                          </div>
                        </div>
                        {tripType === 'round-trip' && (
                          <>
                            <div className="w-px h-10 bg-slate-300 mx-3"></div>
                            <div className="flex-1">
                              <div className="text-xs text-slate-500 font-medium mb-1">Return</div>
                              <span className={cn("font-bold text-lg", returnDate ? "text-slate-800" : "text-slate-400")}>
                                {returnDate ? format(returnDate, 'd MMM') : 'Add date'}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="center">
                    <BigCalendar
                      tripType={tripType === 'round-trip' ? 'round-trip' : 'one-way'}
                      departDate={departDate}
                      returnDate={returnDate}
                      onDepartSelect={(date) => setDepartDate(date)}
                      onReturnSelect={(date) => setReturnDate(date)}
                      onSelectionComplete={() => setOpenDatePicker(false)}
                      fares={fares}
                      faresLoading={faresLoading}
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Passengers & Class */}
              <div className="lg:w-[200px]">
                <Popover open={openPassengers} onOpenChange={setOpenPassengers}>
                  <PopoverTrigger asChild>
                    <button className="w-full h-16 sm:h-[72px] px-4 text-left bg-slate-50 hover:bg-slate-100 border-2 border-slate-200 hover:border-brand-400 rounded-xl transition-all focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent">
                      <div className="text-xs text-slate-500 font-medium mb-1">Travellers & class</div>
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-brand-500" />
                        <span className="font-bold text-slate-800 text-lg">{adults + youth + children + infants}</span>
                        <span className="text-slate-500 text-sm">{travelClass === 'economy' ? 'Economy' : travelClass === 'premium_economy' ? 'Premium' : travelClass === 'business' ? 'Business' : 'First'}</span>
                      </div>
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="w-80 p-4" align="end">
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-800">Travellers</h4>
                      
                      {/* Adults */}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-slate-800">Adults</div>
                          <div className="text-xs text-slate-500">16+</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setAdults(Math.max(1, adults - 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center font-semibold">{adults}</span>
                          <button onClick={() => setAdults(Math.min(9, adults + 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Youth */}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-slate-800">Youth</div>
                          <div className="text-xs text-slate-500">12 - &lt;16</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setYouth(Math.max(0, youth - 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center font-semibold">{youth}</span>
                          <button onClick={() => setYouth(Math.min(9, youth + 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-slate-800">Children</div>
                          <div className="text-xs text-slate-500">02 - &lt;12</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setChildren(Math.max(0, children - 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center font-semibold">{children}</span>
                          <button onClick={() => setChildren(Math.min(9, children + 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      {/* Infants */}
                      <div className="flex items-center justify-between py-2">
                        <div>
                          <div className="font-medium text-slate-800">Infants</div>
                          <div className="text-xs text-slate-500">0 - &lt;02</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <button onClick={() => setInfants(Math.max(0, infants - 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-6 text-center font-semibold">{infants}</span>
                          <button onClick={() => setInfants(Math.min(adults, infants + 1))} className="w-8 h-8 rounded-full border-2 border-slate-300 hover:border-brand-500 flex items-center justify-center transition-colors">
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                      </div>

                      <div className="border-t pt-4 mt-4">
                        <h4 className="font-semibold text-slate-800 mb-3">Cabin class</h4>
                        <div className="grid grid-cols-2 gap-2">
                          <button 
                            onClick={() => setTravelClass('economy')} 
                            className={cn("py-2 px-3 rounded-lg text-sm font-medium transition-all", travelClass === 'economy' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}
                          >
                            Economy
                          </button>
                          <button 
                            onClick={() => setTravelClass('premium_economy')} 
                            className={cn("py-2 px-3 rounded-lg text-sm font-medium transition-all", travelClass === 'premium_economy' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}
                          >
                            Premium
                          </button>
                          <button 
                            onClick={() => setTravelClass('business')} 
                            className={cn("py-2 px-3 rounded-lg text-sm font-medium transition-all", travelClass === 'business' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}
                          >
                            Business
                          </button>
                          <button 
                            onClick={() => setTravelClass('first')} 
                            className={cn("py-2 px-3 rounded-lg text-sm font-medium transition-all", travelClass === 'first' ? 'bg-brand-600 text-white' : 'bg-slate-100 text-slate-700 hover:bg-slate-200')}
                          >
                            First
                          </button>
                        </div>
                      </div>

                      <Button onClick={() => setOpenPassengers(false)} className="w-full bg-brand-600 hover:bg-brand-700 text-white">
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Options Row */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <label htmlFor="direct-flights-hero" className="flex items-center gap-2 cursor-pointer group">
                <Checkbox 
                  id="direct-flights-hero"
                  data-testid="direct-flights-checkbox"
                  checked={directFlights} 
                  onCheckedChange={setDirectFlights}
                  className="border-slate-300 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-800">Direct flights only</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer group">
                <Checkbox 
                  checked={flexiDates} 
                  onCheckedChange={setFlexiDates}
                  className="border-slate-300 data-[state=checked]:bg-brand-600 data-[state=checked]:border-brand-600"
                />
                <span className="text-sm text-slate-600 group-hover:text-slate-800">±3 days flexible</span>
              </label>

              {/* Preferred Airline */}
              <Popover open={openAirline} onOpenChange={setOpenAirline}>
                <PopoverTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-slate-100 hover:bg-slate-200 text-sm text-slate-600 transition-colors">
                    <Plane className="h-4 w-4" />
                    {selectedAirline ? selectedAirline.name : 'Any airline'}
                    <ChevronDown className="h-3 w-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0">
                  <Command filter={(value, search) => {
                    const searchLower = search.toLowerCase().trim();
                    const valueLower = value.toLowerCase();
                    if (valueLower.includes(searchLower)) return 1;
                    const words = valueLower.split(' ');
                    if (words.some(word => word.startsWith(searchLower))) return 1;
                    return 0;
                  }}>
                    <CommandInput placeholder="Search airline..." />
                    <CommandEmpty>No airline found.</CommandEmpty>
                    <CommandGroup className="max-h-56 overflow-auto">
                      <CommandItem
                        value="any airline"
                        onSelect={() => { setSelectedAirline(null); setOpenAirline(false); }}
                      >
                        Any airline
                      </CommandItem>
                      {airlines.map(airline => (
                        <CommandItem
                          key={airline.code}
                          value={`${airline.code} ${airline.name} ${airline.country || ''}`}
                          onSelect={() => { setSelectedAirline(airline); setOpenAirline(false); }}
                        >
                          <span className="font-medium">{airline.code}</span> - {airline.name}
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Search Button */}
            <Button 
              onClick={handleSearch} data-testid="search-flights-btn"
              disabled={!fromAirport || !toAirport || !departDate || (tripType === 'round-trip' && !returnDate)}
              className="w-full h-14 bg-[#FF6600] hover:bg-[#E65C00] text-white text-lg font-semibold rounded-xl shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-6 h-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search flights
            </Button>
          </TabsContent>
        )}

        {/* Multi-City Tab */}

        {tripType === 'multi-city' && (
          <TabsContent value="multi-city" className="space-y-4">
            {multiCityLegs.map((leg, index) => (
              <Card key={`multi-city-leg-${index}`} className="p-4 bg-slate-50 border-slate-200">
                <div className="flex items-center gap-2 mb-3">
                  <Badge variant="secondary" className="bg-brand-100 text-brand-700">Journey {index + 1}</Badge>
                </div>
                <div className="flex items-start gap-4">
                  <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* From */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">From</Label>
                      <Popover 
                        open={isMultiCityPopoverOpen(index, 'from')} 
                        onOpenChange={(open) => setMultiCityPopoverOpen(index, 'from', open)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 justify-start text-left font-normal"
                            onClick={() => setMultiCityPopoverOpen(index, 'from', true)}
                            data-testid={`mc-leg-${index}-from`}
                          >
                            <MapPin className="mr-2 h-4 w-4 text-slate-500" />
                            {leg.from ? (
                              <span className="text-sm font-medium">
                                {leg.from.isGroup ? leg.from.name : `${leg.from.code} - ${leg.from.city}`}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-sm">Select departure</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[350px] p-0" align="start" sideOffset={4}>
                          <Command shouldFilter={false}>
                            <CommandInput 
                              placeholder="Search airport or city..." 
                              value={multiCitySearchTerms[`${index}-from`] || ''}
                              onValueChange={(value) => updateMultiCitySearchTerm(index, 'from', value)}
                            />
                            <CommandEmpty>No airport found. Try another search.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {getFilteredMultiCityAirports(index, 'from').map((option) => (
                                <CommandItem
                                  key={option.isGroup ? `group-${index}-${option.code}` : `airport-${index}-${option.code}`}
                                  value={option.code}
                                  onSelect={() => {
                                    console.log('Selected airport:', option);
                                    updateMultiCityLeg(index, 'from', option);
                                    updateMultiCitySearchTerm(index, 'from', '');
                                    setMultiCityPopoverOpen(index, 'from', false);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {option.isGroup ? option.name : `${option.code} - ${option.city}`}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                      {option.isGroup ? `All ${option.city} airports` : `${option.name}, ${option.country || ''}`}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* To */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">To</Label>
                      <Popover
                        open={isMultiCityPopoverOpen(index, 'to')} 
                        onOpenChange={(open) => setMultiCityPopoverOpen(index, 'to', open)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className="w-full h-12 justify-start text-left font-normal"
                            onClick={() => setMultiCityPopoverOpen(index, 'to', true)}
                            data-testid={`mc-leg-${index}-to`}
                          >
                            <Plane className="mr-2 h-4 w-4 text-slate-500" />
                            {leg.to ? (
                              <span className="text-sm font-medium">
                                {leg.to.isGroup ? leg.to.name : `${leg.to.code} - ${leg.to.city}`}
                              </span>
                            ) : (
                              <span className="text-slate-500 text-sm">Select destination</span>
                            )}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[350px] p-0" align="start" sideOffset={4}>
                          <Command shouldFilter={false}>
                            <CommandInput 
                              placeholder="Search airport or city..." 
                              value={multiCitySearchTerms[`${index}-to`] || ''}
                              onValueChange={(value) => updateMultiCitySearchTerm(index, 'to', value)}
                            />
                            <CommandEmpty>No airport found. Try another search.</CommandEmpty>
                            <CommandGroup className="max-h-64 overflow-auto">
                              {getFilteredMultiCityAirports(index, 'to').map((option) => (
                                <CommandItem
                                  key={option.isGroup ? `group-${index}-${option.code}` : `airport-${index}-${option.code}`}
                                  value={option.code}
                                  onSelect={() => {
                                    console.log('Selected destination:', option);
                                    updateMultiCityLeg(index, 'to', option);
                                    updateMultiCitySearchTerm(index, 'to', '');
                                    setMultiCityPopoverOpen(index, 'to', false);
                                  }}
                                  className="cursor-pointer"
                                >
                                  <div className="flex flex-col">
                                    <span className="font-medium">
                                      {option.isGroup ? option.name : `${option.code} - ${option.city}`}
                                    </span>
                                    <span className="text-xs text-slate-500">
                                      {option.isGroup ? `All ${option.city} airports` : `${option.name}, ${option.country || ''}`}
                                    </span>
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {/* Date */}
                    <div className="space-y-2">
                      <Label className="text-sm font-medium">Departure</Label>
                      <Popover
                        open={isMultiCityPopoverOpen(index, 'date')}
                        onOpenChange={(open) => setMultiCityPopoverOpen(index, 'date', open)}
                      >
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            className={cn(
                              "w-full h-12 justify-start text-left font-normal",
                              !leg.date && "text-slate-500"
                            )}
                            onClick={() => setMultiCityPopoverOpen(index, 'date', true)}
                            data-testid={`mc-leg-${index}-date`}
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {leg.date ? format(leg.date, 'PP') : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start" sideOffset={4}>
                          <Calendar
                            mode="single"
                            selected={leg.date}
                            defaultMonth={index > 0 && multiCityLegs[index - 1]?.date 
                              ? new Date(multiCityLegs[index - 1].date) 
                              : undefined}
                            onSelect={(date) => {
                              if (date) {
                                updateMultiCityLeg(index, 'date', date);
                                setMultiCityPopoverOpen(index, 'date', false);
                                
                                // Auto-open next journey's date picker after a short delay
                                if (index < multiCityLegs.length - 1) {
                                  setTimeout(() => {
                                    // Check if next leg needs a date and has airports selected
                                    const nextLeg = multiCityLegs[index + 1];
                                    if (!nextLeg?.date && nextLeg?.from && nextLeg?.to) {
                                      setMultiCityPopoverOpen(index + 1, 'date', true);
                                    }
                                  }, 300);
                                }
                              }
                            }}
                            disabled={(date) => {
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              if (date < today) return true;
                              if (index > 0) {
                                const prevLeg = multiCityLegs[index - 1];
                                if (prevLeg?.date) {
                                  const prevDate = new Date(prevLeg.date);
                                  prevDate.setHours(0, 0, 0, 0);
                                  return date < prevDate;
                                }
                              }
                              return false;
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  </div>

                  {/* Remove button */}
                  {multiCityLegs.length > 2 && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="mt-8"
                      onClick={() => removeMultiCityLeg(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}

            {/* Add More Button */}
            {multiCityLegs.length < 5 && (
              <Button
                variant="outline"
                onClick={addMultiCityLeg}
                className="w-full h-12 border-dashed border-2 hover:bg-brand-50 hover:border-brand-600 hover:text-brand-700"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Another Flight
              </Button>
            )}

            {/* Passengers, Class, Airline, and Options - Same as Round Trip */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Passengers */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Travellers</Label>
                <Popover open={openPassengers} onOpenChange={setOpenPassengers}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full h-12 justify-between text-left font-normal"
                    >
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4 text-slate-500" />
                        <span>{totalPassengers} Traveller{totalPassengers > 1 ? 's' : ''}</span>
                      </div>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[320px] p-4" align="start">
                    <div className="space-y-4">
                      {/* Adults */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Adults</div>
                          <div className="text-xs text-slate-500">+12 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setAdults(Math.max(1, adults - 1))}
                            disabled={adults <= 1}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{adults}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setAdults(Math.min(9, adults + 1))}
                            disabled={adults >= 9}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Youth */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Youth</div>
                          <div className="text-xs text-slate-500">12-18 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setYouth(Math.max(0, youth - 1))}
                            disabled={youth <= 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{youth}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setYouth(Math.min(9, youth + 1))}
                            disabled={youth >= 9}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Children */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Children</div>
                          <div className="text-xs text-slate-500">2–under 12 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setChildren(Math.max(0, children - 1))}
                            disabled={children <= 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{children}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setChildren(Math.min(9, children + 1))}
                            disabled={children >= 9}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>

                      {/* Infants */}
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-medium">Infants</div>
                          <div className="text-xs text-slate-500">0–under 2 years</div>
                        </div>
                        <div className="flex items-center gap-3">
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setInfants(Math.max(0, infants - 1))}
                            disabled={infants <= 0}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <span className="w-8 text-center font-medium">{infants}</span>
                          <Button
                            variant="outline"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => setInfants(Math.min(adults, infants + 1))}
                            disabled={infants >= adults}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      {/* Age Calculation Note */}
                      <div className="text-xs text-slate-500 italic border-t pt-2 mt-2">
                        Passenger age will be calculated based on the return date of travel.
                      </div>

                      <Button
                        onClick={() => setOpenPassengers(false)}
                        className="w-full bg-brand-600 hover:bg-brand-700"
                      >
                        Done
                      </Button>
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Travel Class */}
              <div className="space-y-2">
                <Label className="text-sm font-medium">Class</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-12 justify-between">
                      <span className="capitalize">{travelClass.replace('-', ' ')}</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[200px] p-2">
                    <div className="space-y-1">
                      {['economy', 'premium-economy', 'business', 'first'].map((classType) => (
                        <Button
                          key={classType}
                          variant={travelClass === classType ? 'default' : 'ghost'}
                          className="w-full justify-start"
                          onClick={() => setTravelClass(classType)}
                        >
                          {classType === 'premium-economy' ? 'Premium Economy' : 
                           classType === 'first' ? 'First Class' : 
                           classType === 'business' ? 'Business Class' : 'Economy'}
                        </Button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Airline Selector */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Preferred Airline (Optional)</Label>
              <Popover open={openAirline} onOpenChange={setOpenAirline}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full h-12 justify-between text-left font-normal"
                  >
                    <span className={selectedAirline ? '' : 'text-slate-500'}>
                      {selectedAirline ? `${selectedAirline.code} - ${selectedAirline.name}` : 'All Airlines'}
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[350px] p-0">
                  <Command filter={(value, search) => {
                    // Custom filter function for airline search
                    const searchLower = search.toLowerCase().trim();
                    const valueLower = value.toLowerCase();
                    
                    // Match if search term is found in code, name, or country
                    if (valueLower.includes(searchLower)) return 1;
                    
                    // Also match partial words
                    const words = valueLower.split(' ');
                    if (words.some(word => word.startsWith(searchLower))) return 1;
                    
                    return 0;
                  }}>
                    <CommandInput placeholder="Search airline code or name..." />
                    <CommandEmpty>No airline found.</CommandEmpty>
                    <CommandGroup className="max-h-64 overflow-auto">
                      <CommandItem
                        value="all airlines"
                        onSelect={() => {
                          setSelectedAirline(null);
                          setOpenAirline(false);
                        }}
                      >
                        <span className="font-medium">All Airlines</span>
                      </CommandItem>
                      {airlines.map((airline) => (
                        <CommandItem
                          key={airline.code}
                          value={`${airline.code} ${airline.name} ${airline.country || ''}`}
                          onSelect={() => {
                            setSelectedAirline(airline);
                            setOpenAirline(false);
                          }}
                        >
                          <div className="flex flex-col">
                            <span className="font-medium">{airline.code} - {airline.name}</span>
                            <span className="text-xs text-slate-500">{airline.country}</span>
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </Command>
                </PopoverContent>
              </Popover>
            </div>

            {/* Options */}
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="direct-flights-mc"
                  checked={directFlights}
                  onCheckedChange={setDirectFlights}
                />
                <label
                  htmlFor="direct-flights-mc"
                  className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  Direct flights only
                </label>
              </div>
            </div>

            <div className="flex justify-center">
              <Button 
                onClick={handleSearch} data-testid="search-multi-city-btn"
                className="w-full sm:w-auto sm:min-w-[200px] h-12 text-base font-semibold bg-[#FF6600] hover:bg-[#E65C00] text-white transition-colors"
                disabled={multiCityLegs.filter(leg => leg.from && leg.to && leg.date).length < 2}
              >
                Search Multi-City Flights
              </Button>
            </div>
          </TabsContent>
        )}
        
        {/* Umrah tab - opens the Umrah Packages flow */}
        {tripType === 'umrah' && (
          <TabsContent value="umrah" className="text-center py-8">
            <div className="space-y-6">
              <div className="text-6xl">🕋</div>
              <h3 className="text-xl font-bold text-slate-900">Umrah Packages</h3>
              <p className="text-slate-600 max-w-md mx-auto">
                Search once and get complete, ready-to-book Umrah packages — flights, hotels within 700 m of the Haram, transfers and visa.
              </p>
              <div className="flex justify-center gap-4 text-sm text-slate-500">
                <span className="flex items-center gap-1">Flights</span>
                <span className="flex items-center gap-1">🏨 Hotels</span>
                <span className="flex items-center gap-1">🚗 Transport</span>
                <span className="flex items-center gap-1">Ziyarat</span>
              </div>
              <Button
                onClick={() => onSearch({ tripType: 'umrah' })}
                data-testid="umrah-start-builder-btn"
                className="bg-[#FF6600] hover:bg-[#E65C00] text-white h-12 px-8"
              >
                <Plane className="mr-2 h-5 w-5" />
                Start Building Your Umrah Package
              </Button>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </Card>
  );
};
